import { Layout } from "@/components/layout/Layout";
import { ScrollFadeIn } from "@/components/ScrollFadeIn";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const BG_IMAGE = "/cases/pordenone/hero.jpg";

/* ─── Persistent background layer ─────────────────────────────────── */
function PageBackground() {
  return (
    <div className="fixed inset-0 -z-10">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-background/[0.82]" />
    </div>
  );
}

/* ─── Section 1 · Hero ──────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative flex min-h-[85vh] items-center justify-center px-6 py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/90" />
      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
            Materials, defined&nbsp;with&nbsp;intent.
          </h1>
        </ScrollFadeIn>
        <ScrollFadeIn delay={120}>
          <p className="mx-auto mt-8 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
            Materials are not selections made at the end. They define tone,
            texture, light, and how a space is ultimately experienced. MINZO
            helps designers source materials that remain consistent — from
            concept to construction.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={240}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start a material inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 2 · Intro ─────────────────────────────────────────────── */
function Intro() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              More than choosing finishes.
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                Materials often drift during execution. Tone shifts between
                samples and installation. Pattern direction gets lost. Finishes
                arrive inconsistent across batches. Suppliers misalign with the
                design intent, and what was specified becomes something else
                entirely.
              </p>
              <p>
                MINZO helps designers maintain material precision from the first
                concept board through to final installation — so the space that's
                built is the space that was designed.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 3 · Material Scope ─────────────────────────────────── */
const scopes = [
  {
    title: "Hard Finishes",
    body: "Natural stone, engineered surfaces, tiles, wood flooring, and metal trims — selected for visual weight, veining consistency, and how they hold light across a space.",
  },
  {
    title: "Soft Materials",
    body: "Textiles, leathers, wall coverings, and acoustic fabrics — chosen for tactile quality, colour fastness, and coherence with surrounding hard surfaces.",
  },
  {
    title: "Custom Materials",
    body: "Bespoke finishes, custom-coloured surfaces, tailored stone cuts, and specialist fabrication — coordinated with production partners when standard options don't meet the design.",
  },
  {
    title: "Cross-System Alignment",
    body: "Ensuring material selections across flooring, walls, joinery, furniture, and lighting share the same tonal language — so the space reads as a single, resolved composition.",
  },
];

function MaterialScope() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Material scope
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {scopes.map((s, i) => (
            <ScrollFadeIn key={s.title} delay={i * 100}>
              <div>
                <h3 className="font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 4 · Why Materials Matter ───────────────────────────── */
function WhyMaterialsMatter() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              Why materials matter
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                Materials define the edges of a room. They shape how light falls,
                how sound moves, how warmth is held or released. They set the
                visual tempo — the contrast between a polished floor and a matte
                wall, the grain direction that leads the eye, the depth that a
                natural stone surface carries in changing daylight.
              </p>
              <p>
                When materials are chosen with intent, a space feels inevitable.
                When they're chosen loosely, even well-designed rooms feel
                provisional — surfaces that coexist rather than compose.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 5 · Typical Problems ───────────────────────────────── */
const problems = [
  "Samples approved in the studio look different once installed at scale.",
  "Finishes arrive inconsistent across production batches or slabs.",
  "Sourcing the right stone or timber becomes a prolonged, uncertain process.",
  "Variation in natural materials — veining, grain, tone — lacks control.",
  "Compromises due to local supplier availability erode the original intent.",
  "Materials selected individually don't work together as a unified palette.",
];

function TypicalProblems() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Typical problems we help solve
          </h2>
        </ScrollFadeIn>
        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((p, i) => (
            <ScrollFadeIn key={i} delay={i * 80}>
              <li className="flex items-start gap-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                {p}
              </li>
            </ScrollFadeIn>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── Section 6 · How We Work ────────────────────────────────────── */
const steps = [
  { num: "01", title: "Understand material intent", body: "We start with the spatial concept — light, atmosphere, tonal direction — and clarify what the materials need to achieve, not just what they need to look like." },
  { num: "02", title: "Narrow the selection", body: "From an informed shortlist, we refine options based on performance, availability, batch consistency, and how each material relates to its neighbours." },
  { num: "03", title: "Match and verify", body: "Physical samples are matched under real conditions. We verify tone, texture, and scale — and confirm consistency before procurement begins." },
  { num: "04", title: "Support execution", body: "From ordering through delivery and installation coordination, we maintain material control so the finished space matches the approved palette." },
];

function HowWeWork() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            How we work
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {steps.map((s, i) => (
            <ScrollFadeIn key={s.num} delay={i * 100}>
              <div>
                <span className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-primary/60">
                  {s.num}
                </span>
                <h3 className="mt-2 font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 7 · Material Categories ────────────────────────────── */
const categories = [
  "Natural stone",
  "Tiles & ceramics",
  "Wood & veneers",
  "Metal finishes",
  "Wall finishes",
  "Flooring systems",
  "Textiles & leather",
  "Custom surfaces",
];

function MaterialCategories() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Material categories
          </h2>
        </ScrollFadeIn>
        <div className="mt-14 flex flex-wrap gap-3">
          {categories.map((c, i) => (
            <ScrollFadeIn key={c} delay={i * 50}>
              <span className="inline-block border border-border/60 bg-background/50 backdrop-blur-sm px-5 py-2.5 font-sans text-xs uppercase tracking-[0.12em] text-foreground/70">
                {c}
              </span>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 8 · Brand Positioning ──────────────────────────────── */
function BrandPositioning() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              For designers who care about material&nbsp;precision
            </h2>
            <p className="mt-8 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              Restraint over variety. Depth over breadth. Consistency over
              convenience. The right material palette doesn't impress — it
              resolves. Every surface, every edge, every texture should feel
              deliberate, quiet, and aligned with the spatial intent from the
              very beginning.
            </p>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 9 · Final CTA ──────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/70 to-background/80" />
      <div className="relative mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Working on a material-led project?
          </h2>
        </ScrollFadeIn>
        <ScrollFadeIn delay={100}>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base leading-relaxed text-muted-foreground">
            Share your drawings, material references, or concept direction. We
            can support selection, verification, and sourcing coordination
            based on the needs of the project.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={200}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start a material inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Page ────────────────────────────────────────────────────────── */
export default function MaterialsSolutions() {
  return (
    <Layout>
      <PageBackground />
      <Hero />
      <Intro />
      <MaterialScope />
      <WhyMaterialsMatter />
      <TypicalProblems />
      <HowWeWork />
      <MaterialCategories />
      <BrandPositioning />
      <FinalCTA />
    </Layout>
  );
}
